import * as GC from '@grapecity/spread-sheets';
import { IProduct, IProductTag, ISetUpProduct } from '@models/product';
import { ProductStatus } from '@models/record-status';

export const cellIsActionButton = (
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number,
  sheetFilteredSetups: IProduct[]
): boolean => {
  const isSpreadArea = typeof row === 'number' && typeof col === 'number' && row > 1 - 1 && col > -1;

  const sheetSetupsLength = sheetFilteredSetups.length;
  const isActionButton =
    isSpreadArea &&
    // mouse is over one of the action button cells
    col > sheet.getColumnCount() - 4 &&
    // Is a row of an existing record
    row - 1 < sheetSetupsLength;

  return isActionButton;
};

/**
 * Takes a sheet and an on click event and returns a spread range object
 */
export const getSelectionFromMouseEvent = (sheet: GC.Spread.Sheets.Worksheet, event) => {
  const selection = sheet.getSelections()[0];
  if (selection) {
    const { rowCount, colCount } = selection;
    const hitTest = sheet.hitTest(event.layerX, event.layerY);
    let { row, col } = hitTest;
    if (hitTest.hitTestType === 1) {
      row = -1;
    }
    if (hitTest.hitTestType === 2) {
      col = -1;
    }

    return { row, col, rowCount, colCount };
  } else {
    return { row: 0, col: 0, rowCount: 0, colCount: 0 };
  }
};

export const lockRow = (sheet: GC.Spread.Sheets.Worksheet, row: number) => {
  const range = sheet.getRange(row, 0, 1, 9);
  range.locked(true);
};

export const unlockRow = (sheet: GC.Spread.Sheets.Worksheet, row: number) => {
  const range = sheet.getRange(row, 0, 1, 9);
  range.locked(false);
};

// /**
//  * Sets the bottom empty rows and adds the water mark to them
//  */
// export const setBottomEmptyRows = (
//   sheet: GC.Spread.Sheets.Worksheet,
//   COLUMNS: any[],
//   numRows: number,
//   products: IProduct[],
//   applyBorder = false
// ) => {
//   sheet.setRowCount(sheet.getRowCount() + numRows);
//   COLUMNS.forEach((x, ind) => {
//     sheet.getRange(sheet.getRowCount() - numRows, ind, numRows, 1).watermark(x.key);
//     // sheet.getCell(sheet.getRowCount() - 1, ind).watermark(x.key);
//     // sheet.getCell(sheet.getRowCount() - 2, ind).watermark(x.key);
//   });
//   if (applyBorder) {
//     const border = new GC.Spread.Sheets.LineBorder();
//     border.color = 'black';
//     border.style = GC.Spread.Sheets.LineStyle.mediumDashed;
//     const startRow = products.filter(x => x.ProductId !== -1).length;
//     sheet.getRange(startRow + 1, 0, 1, sheet.getColumnCount()).borderTop(border);
//   }
// };

export const setBottomEmptyRows = (sheet: GC.Spread.Sheets.Worksheet, numRows: number, applyBorder: boolean = false): number => {
  if (sheet) {
    const currentRows = sheet.getRowCount();
    const content = sheet.getArray(0, 0, currentRows, sheet.getColumnCount());

    let indexOfLastNonEmptyProduct = -1;
    for (let i = currentRows - 1; i >= 0; i--) {
      if (content[i].some(e => e)) {
        indexOfLastNonEmptyProduct = i;
        break;
      }
    }

    const difference = currentRows - (indexOfLastNonEmptyProduct + 1);
    let rowsToBeAdded = 0;
    if (difference <= 1) {
      rowsToBeAdded = difference === 1 ? 1 : numRows;
      sheet.setRowCount(currentRows + rowsToBeAdded);

      const newEditableRange = sheet.getRange(indexOfLastNonEmptyProduct + 1, 0, currentRows + rowsToBeAdded, sheet.getColumnCount());
      if (newEditableRange) {
        newEditableRange.locked(false);
      }
    }

    if (applyBorder) {
      const border = new GC.Spread.Sheets.LineBorder();
      border.color = 'black';
      border.style = GC.Spread.Sheets.LineStyle.mediumDashed;
      const startRow = indexOfLastNonEmptyProduct + 1;
      sheet.getRange(startRow, 0, 1, sheet.getColumnCount()).borderTop(border);
    }

    return rowsToBeAdded;
  }
};

export const getProductFromRow = (
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  productTypes: IProductTag[],
  counterPartyTypes: IProductTag[]
): ISetUpProduct => {
  try {
    let counterPartyType;
    let counterPartyText;
    const counterPartyValueFromSheet = sheet.getCell(row, 4).text();
    const counterParty = counterPartyTypes.find(x => x.Value === counterPartyValueFromSheet);
    if (counterPartyValueFromSheet !== null && counterParty) {
      counterPartyType = counterParty.Key;
      counterPartyText = counterParty.Value;
    }
    let productType;
    let productTypeText;
    const productTypeValue = sheet.getCell(row, 3).text();
    if (productTypeValue && productTypes.find(x => x.Value === productTypeValue)) {
      if (productTypes.find(x => x.Value === productTypeValue)) {
        productType = productTypes.find(x => x.Value === productTypeValue).Key;
        productTypeText = productTypes.find(x => x.Value === productTypeValue).Value;
      }
    }

    const product: IProduct = {
      ProductName: sheet.getCell(row, 0).text() || null,
      Mnemonic: sheet.getCell(row, 1).text() || null,
      ProductNumber: sheet.getCell(row, 2).value(),
      ProductType: productType,
      ProductTypeText: productTypeText,
      CounterpartyType: counterPartyType,
      CounterpartyText: counterPartyText,
      Description: sheet.getCell(row, 5).text() || null,
      ProductGroupId: null,
      ProductGroupName: sheet.getCell(row, 6).text() || null,
      DisplayOrder: sheet.getCell(row, 7).value() === null ? 0 : sheet.getCell(row, 7).value(),
      SubProducts: sheet
        .getCell(row, 8)
        .text()
        .replaceAll(' ', ''),
      ProductId: -1,
      FormId: null,
      DateTimeCreated: null,
      DateTimeModified: null,
      UserIdCreated: -1,
      UserIdModified: -1,
      RecordStatus: ProductStatus.Active
    };
    return product;
  } catch (e) {
    console.error(e);
    return null;
  }
};

/**
 *
 * @param product The current value for a given product
 * @param productDB A DB copy of the product
 * @param propertiesToCheck An array of IProduct properties that can be checked for changes
 * @returns true if there is any changes from the DB product to the Product
 */
export const hasProductChanges = (product: ISetUpProduct, productDB: ISetUpProduct, propertiesToCheck?: string[]) => {
  let hasChanges = false;
  // List of items we are checking for changes
  const properties = [
    'ProductName',
    'Mnemonic',
    'ProductNumber',
    'ProductType',
    'CounterpartyType',
    'CounterpartyText',
    'Description',
    'ProductGroupName',
    'DisplayOrder',
    'SubProducts',
    'RecordStatus',
    'ToBeReactivated',
    'ToBeDeleted',
    'TabId',
    'ToBeDeactivated',
    'SubProducts'
  ];

  if (product && productDB) {
    if (!propertiesToCheck) {
      for (let i = 0; i < properties.length; i++) {
        const property = properties[i];

        if (product[property] !== productDB[property]) {
          hasChanges = true;
          break;
        }
      }
    } else {
      for (let i = 0; i < propertiesToCheck.length; i++) {
        const property = propertiesToCheck[i];
        if (product[property] !== productDB[property]) {
          hasChanges = true;
          break;
        }
      }
    }

    // if (product.ProductName !== productDB.ProductName && !hasChanges) {
    //   hasChanges = true;
    // } else if (product.Mnemonic !== productDB.Mnemonic && !hasChanges) {
    //   hasChanges = true;
    // } else if (product.ProductNumber !== productDB.ProductNumber && !hasChanges) {
    //   hasChanges = true;
    // } else if (product.ProductType !== productDB.ProductType && !hasChanges) {
    //   hasChanges = true;
    // } else if (product.CounterpartyType !== productDB.CounterpartyType && !hasChanges) {
    //   hasChanges = true;
    // } else if (product.Description !== productDB.Description && !hasChanges) {
    //   hasChanges = true;
    // } else if (product.ProductGroupName !== productDB.ProductGroupName && !hasChanges) {
    //   hasChanges = true;
    // } else if (product.DisplayOrder !== productDB.DisplayOrder && !hasChanges) {
    //   hasChanges = true;
    // } else if (product.SubProducts !== productDB.SubProducts && !hasChanges) {
    //   hasChanges = true;
    // } else if (product.RecordStatus !== productDB.RecordStatus && !hasChanges) {
    //   hasChanges = true;
    // } else if (product.ToBeReactivated !== productDB.ToBeDeactivated && !hasChanges) {
    //   hasChanges = true;
    // } else if (product.TabId !== productDB.TabId && !hasChanges) {
    //     hasChanges = true;
    // }
  } else {
    hasChanges = true;
  }

  return hasChanges;
};

export const clearWorkBook = (spread: GC.Spread.Sheets.Workbook) => {
  const wasSuspended = spread.isPaintSuspended();

  if (!wasSuspended) {
    spread.suspendPaint();
    spread.suspendEvent();
  }

  const numSheets = spread.sheets.length;
  for (let i = 0; i < numSheets; i++) {
    spread.removeSheet(0);
  }

  if (!wasSuspended) {
    spread.resumeEvent();
    spread.resumePaint();
  }
};
